/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { generatePath } from "react-router-dom";
import { AppEnvironment } from "~/components/ApplicationItem/Types";
import { getBudgetValue } from "~/components/Budgets";
import { ROUTES } from "~/routing/routes";
import { formatMoneyWithFallback, extractNumFromString } from "~/tools";

import { OrgUnitApplication } from "../../types";
import { SORT_FILTERS } from "./constants";

const appCostChange = (forecast: number, previous: number) =>
  previous > 0 ? (forecast / previous) * 100 : 0;

export function mapApplications(
  applications: OrgUnitApplication[],
  currency: string
) {
  const currentMonthIndex = dayjs().month();

  return applications.map((application) => {
    const envs = application.environments.map((environment) => ({
      environment: environment.name || "",
      budget: formatMoneyWithFallback(
        getBudgetValue(
          environment.budgetYearly?.budgetByMonth,
          currentMonthIndex
        ),
        currency
      ),
      id: environment.id,
      resources: environment.resourceCount,
      cloudWasteAggregate: environment.cloudWasteAggregate,
      url: generatePath(ROUTES.applications.environment, {
        application: application.id,
        environment: environment.id,
      }),
      actualCosts: environment.cost?.currentMonth ?? "",
      forecast: environment.cost?.forecast ?? "",
      belongsTo: null,
    }));

    return {
      name: application?.name,
      id: application?.id,
      hasAnomalies: false,
      resources:
        application.environments.reduce(
          (acc: number, env) => acc + (env?.resourceCount ?? 0),
          0
        ) ?? 0,
      budget: formatMoneyWithFallback(
        getBudgetValue(
          application.budgetYearly?.budgetByMonth,
          currentMonthIndex
        ),
        currency
      ),
      cost: {
        currentMonth: formatMoneyWithFallback(
          application?.cost?.currentMonth,
          currency
        ),
        forecast: formatMoneyWithFallback(
          application?.cost?.forecast,
          currency
        ),
        forecastToPrev: appCostChange(
          parseFloat(application?.cost?.forecast || "0"),
          parseFloat(
            application?.budgetYearly?.budgetByMonth?.[currentMonthIndex] ?? "0"
          )
        ),
        previousMonth: formatMoneyWithFallback(
          application?.cost?.previousMonth,
          currency
        ),
        appMonthlyCosts: formatMoneyWithFallback(
          sumKey(envs, "actualCosts"),
          currency
        ),
        appMonthlyForecast: formatMoneyWithFallback(
          String(sumKey(envs, "forecast")),
          currency
        ),
      },
      // Separate formatting is needed here since unformatted values are used to calculate `appMonthlyCosts` and `appMonthlyForecast`
      envs: envs.map((env) => ({
        ...env,
        actualCosts: formatMoneyWithFallback(env.actualCosts, currency),
        forecast: formatMoneyWithFallback(env.forecast, currency),
      })),
    };
  });
}

function sumKey(
  envs: AppEnvironment[],
  key: Extract<keyof AppEnvironment, "actualCosts" | "forecast">
): number {
  return (
    envs.reduce((a, b) => Number(a) + Number(String(b[key] ?? "0")), 0) ?? 0
  );
}

export type MappedApp = ReturnType<typeof mapApplications>[number];

type SortableApp = {
  id: string;
  name: string;
  cost: {
    appMonthlyCosts?: string;
    appMonthlyForecast?: string;
    currentMonth: string;
    forecast: string;
    previousMonth: string;
  };
  resources: number;
  envs: AppEnvironment[];
};

export function getApplications<T extends SortableApp>(
  apps: T[],
  filter: SORT_FILTERS
) {
  switch (filter) {
    case SORT_FILTERS.app_name:
      return apps.sort((a, b) => a.name.localeCompare(b.name));
    case SORT_FILTERS.monthly_costs:
      return apps.sort((a, b) =>
        extractNumFromString(a.cost.appMonthlyCosts ?? "0") >=
        extractNumFromString(b.cost.appMonthlyCosts ?? "0")
          ? 1
          : -1
      );
    case SORT_FILTERS.monthly_forecast_costs:
      return apps.sort((a, b) =>
        extractNumFromString(a.cost.appMonthlyForecast ?? "0") >=
        extractNumFromString(b.cost.appMonthlyForecast ?? "0")
          ? 1
          : -1
      );
    case SORT_FILTERS.prev_month_costs:
      return apps.sort((a, b) =>
        extractNumFromString(a.cost.previousMonth) >=
        extractNumFromString(b.cost.previousMonth)
          ? 1
          : -1
      );
    case SORT_FILTERS.resources:
      return apps.sort((a, b) => (a.resources >= b.resources ? 1 : -1));
    case SORT_FILTERS.environments:
      return apps.sort((a, b) =>
        (a.envs?.length ?? "0") >= (b.envs?.length ?? "0") ? 1 : -1
      );
    default:
      throw new Error("Unsupported sort filter.");
  }
}
